import React, { useRef, useState, useCallback, useMemo } from 'react'
import {
  View,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
} from 'react-native'

import { useNavigation, RouteProp, useRoute } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PreviewRegenerateConfirmationModal } from './components/PreviewRegenerateConfirmationModal'
import {
  GetEBookQuery,
  useGetEBookQuery,
  useUpdateEbookMutation,
} from '../../api/types'
import Loading from '../../components/Loading'
import Separator from '../../components/Separator'
import SvgIcon from '../../components/SvgIcon'
import ButtonText from '../../components/Text/ButtonText'
import Typography, { TypographyInput } from '../../components/Text/Typography'
import TouchableSvg from '../../components/TouchableSvg'
import WebModal from '../../components/WebModal'
import { useDirectUpload } from '../../hooks/useDirectUpload'
import { usePickImageActionSheet } from '../../hooks/usePickImageActionSheet'
import useSafeAreaPaddedStyle, {
  headerOptions,
} from '../../hooks/useSafeAreaPaddedStyle'
import { useUser } from '../../providers/UserProvider'
import appStyles from '../../styles/app-styles'
import Colors from '../../styles/Colors'
import { MediaItem } from '../../types/media-types'
import {
  MainStackParamsType,
  MainStackNavigationType,
} from '../../types/navigation-types'
import { convertHexToRGBA } from '../../utils/colors'

type InputItem =
  | 'title'
  | 'subtitle'
  | 'image'
  | 'description'
  | 'backside-image'
  | 'summary'

type InputErrors = { [key in InputItem]?: boolean }

function getImageName(item: MediaItem | undefined, oldUrl: string | undefined) {
  return (
    item?.fileName ||
    (item?.url ?? oldUrl) // fallback to extracting name from path
      ?.split('/')
      .slice(1) // ignore first part - only part if splitting failed
      .slice(-1)[0] // take last
      ?.slice(-50) // limit length to 50
  )
}
export default function EBookSettingsScreen(): JSX.Element {
  const insets = useSafeAreaInsets()
  const { params } = useRoute<RouteProp<MainStackParamsType, 'EBookSettings'>>()
  const { goBack } = useNavigation<MainStackNavigationType<'EBookSettings'>>()
  const { t, i18n } = useTranslation(undefined, {
    keyPrefix: 'screens.ebookSettings',
  })

  const { user } = useUser()
  const { uploadMedia } = useDirectUpload()
  const [updateEbookMutation, { client }] = useUpdateEbookMutation()

  const [isSaving, setIsSaving] = useState(false)
  const [focus, setFocus] = useState<InputItem>()
  const [errors, setErrors] = useState<InputErrors>({})
  const [
    isPreviewRegenerateConfimationModalVisible,
    setIsPreviewRegenerateConfimationModalVisible,
  ] = useState(false)
  const needsRegenerateConfirmation = useRef(false)

  const [title, setTitle] = useState<string>('')
  const [summary, setSummary] = useState<string>('')
  const [subtitle, setSubtitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const coverImagePicker = usePickImageActionSheet('coverImage')
  const [oldCoverImageUrl, setOldCoverImageUrl] = useState<string>()
  const backsideImagePicker = usePickImageActionSheet('backsideImage')
  const [oldBacksideImageUrl, setOldBacksideImageUrl] = useState<string>()

  const { loading } = useGetEBookQuery({
    variables: { id: params.ebookId },
    onCompleted: ({ ebook }: GetEBookQuery) => {
      setTitle(ebook.title ?? '')
      setSummary(ebook.outro ?? '')
      setSubtitle(ebook.subtitle ?? '')
      setDescription(ebook.intro ?? '')
      setOldCoverImageUrl(ebook.coverImageUrl ?? undefined)
      setOldBacksideImageUrl(ebook.backsideCoverImageUrl ?? undefined)

      // Full ebook ready or is being generated
      needsRegenerateConfirmation.current =
        !!ebook.fullFileUrl || (ebook.beingGenerated && !!ebook.previewFileUrl)
    },
  })

  const onFocus = useCallback((item: InputItem) => {
    setFocus(item)
    setErrors(current => ({ ...current, [item]: undefined }))
  }, [])

  const getOnBlur = useCallback(
    (inputItem: InputItem) => () =>
      setFocus(current => (current === inputItem ? undefined : current)),
    [],
  )

  const placeholders = useMemo(
    () => ({
      title: t('titlePlaceholder', {
        name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
      }),
      subtitle: t('subtitlePlaceholder', {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
      }),
      description: t('descriptionPlaceholder', {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
      }),
    }),
    [t, user],
  )

  const submit = useCallback(() => {
    const inputErrors: InputErrors = {}
    if (!title) {
      inputErrors.title = true
    }

    if (!description) {
      inputErrors.description = true
    }

    if (!summary) {
      inputErrors.summary = true
    }

    if (Object.keys(inputErrors).length) {
      return setErrors(inputErrors)
    }

    const uploadIfSelected = (item: MediaItem | undefined) => {
      if (!item) return

      if (item.__typename === 'Attachment')
        return { fileBlobId: item.signedBlobId }

      return uploadMedia(
        item.url ?? '',
        item.description,
        item.fileName,
        item.contentType,
      )
    }

    setIsSaving(true)
    Promise.all([
      uploadIfSelected(coverImagePicker.image),
      uploadIfSelected(backsideImagePicker.image),
    ])
      .then(([coverImageUpload, backsideImageUpload]) => {
        const variables = {
          id: params.ebookId,
          input: {
            title,
            outro: summary,
            subtitle,
            intro: description,
            coverImageBlobId:
              coverImageUpload?.fileBlobId ??
              (!oldCoverImageUrl ? null : undefined),
            backsideCoverImageBlobId:
              backsideImageUpload?.fileBlobId ??
              (!oldBacksideImageUrl ? null : undefined),
          },
        }

        return updateEbookMutation({ variables })
      })
      .then(() => {
        client.cache.evict({
          id: client.cache.identify({
            id: params.ebookId,
            __typename: 'Ebook',
          }),
        })
        client.cache.gc()
        goBack()
      })
  }, [
    title,
    goBack,
    summary,
    subtitle,
    description,
    uploadMedia,
    client.cache,
    params.ebookId,
    oldCoverImageUrl,
    oldBacksideImageUrl,
    updateEbookMutation,
    coverImagePicker.image,
    backsideImagePicker.image,
  ])

  const onSavePress = useCallback(() => {
    if (needsRegenerateConfirmation.current) {
      setIsPreviewRegenerateConfimationModalVisible(true)
    } else {
      submit()
    }
  }, [submit])

  const onCoverImageClearPress = useMemo(() => {
    if (coverImagePicker.image) {
      return coverImagePicker.clear
    }
    if (oldCoverImageUrl) {
      return () => setOldCoverImageUrl(undefined)
    }
  }, [oldCoverImageUrl, coverImagePicker.image, coverImagePicker.clear])

  const onBacksideImageClearPress = useMemo(() => {
    if (backsideImagePicker.image) {
      return backsideImagePicker.clear
    }
    if (oldBacksideImageUrl) {
      return () => setOldBacksideImageUrl(undefined)
    }
  }, [
    oldBacksideImageUrl,
    backsideImagePicker.image,
    backsideImagePicker.clear,
  ])

  const summaryWarning = summary.length > 300 ? t('longOutroWarning') : null

  return (
    <WebModal>
      <View style={styles.container}>
        <Loading loading={loading} />
        <Loading blocking loading={isSaving} />
        <View style={[useSafeAreaPaddedStyle(styles.header, headerOptions)]}>
          <TouchableSvg name="back" color="layout.dark" onPress={goBack} />
          <Separator width={5} />
          <Typography weight="medium" style={styles.titleText}>
            {t('title')}
          </Typography>
          <Separator width={45} />
        </View>
        <ScrollView contentContainerStyle={styles.scrollContentContainer}>
          <Separator height={32} />

          <Typography weight="medium" style={styles.inputLabel}>
            {t('titleLabel')}
          </Typography>
          <Separator height={8} />
          <TypographyInput
            value={title}
            weight="medium"
            maxLength={100}
            placeholderTextColor={placeholderTextColor}
            placeholder={loading ? undefined : placeholders.title}
            style={[
              styles.textInput,
              styles.thinInput,
              focus === 'title' && styles.focusedInput,
              errors?.title && styles.missingInput,
            ]}
            onChangeText={setTitle}
            onBlur={getOnBlur('title')}
            onFocus={() => onFocus('title')}
          />
          <Separator height={24} />
          <Typography weight="medium" style={styles.inputLabel}>
            {t('subtitleLabel')}
          </Typography>
          <Separator height={8} />
          <TypographyInput
            value={subtitle}
            weight="medium"
            maxLength={100}
            placeholderTextColor={placeholderTextColor}
            placeholder={loading ? undefined : placeholders.subtitle}
            style={[
              styles.textInput,
              styles.thinInput,
              focus === 'subtitle' && styles.focusedInput,
              errors?.subtitle && styles.missingInput,
            ]}
            onChangeText={setSubtitle}
            onBlur={getOnBlur('subtitle')}
            onFocus={() => onFocus('subtitle')}
          />
          <Separator height={24} />
          <Typography weight="medium" style={styles.inputLabel}>
            {t('coverImageLabel')}
          </Typography>
          <Separator height={8} />
          <Pressable
            style={({ pressed }) => [
              appStyles.inlineContainer,
              styles.textInput,
              styles.thinInput,
              pressed && styles.pressedInput,
              errors?.image && styles.missingInput,
            ]}
            // eslint-disable-next-line react/jsx-handler-names
            onPress={coverImagePicker.openImagePicker}>
            <Typography style={appStyles.fullSize}>
              {getImageName(coverImagePicker.image, oldCoverImageUrl)}
            </Typography>
            <Pressable
              hitSlop={20}
              style={styles.imageInputIcon}
              disabled={!onCoverImageClearPress}
              onPress={onCoverImageClearPress}>
              <SvgIcon
                name={!onCoverImageClearPress ? 'chevron' : 'close'}
                width={20}
                height={20}
                color={Colors['layout.black']}
              />
            </Pressable>
          </Pressable>
          <Separator height={24} />
          <Typography weight="medium" style={styles.inputLabel}>
            {t('descriptionLabel')}
          </Typography>
          <Separator height={8} />
          <TypographyInput
            multiline
            weight="medium"
            value={description}
            placeholderTextColor={placeholderTextColor}
            placeholder={loading ? undefined : placeholders.description}
            style={[
              styles.textInput,
              styles.descriptionInput,
              focus === 'description' && styles.focusedInput,
              errors.description && styles.missingInput,
            ]}
            onChangeText={setDescription}
            onBlur={getOnBlur('description')}
            onFocus={() => setFocus('description')}
          />
          <Separator height={24} />
          <Typography weight="medium" style={styles.inputLabel}>
            {t('backCoverImageLabel')}
          </Typography>
          <Separator height={8} />
          <Pressable
            style={({ pressed }) => [
              appStyles.inlineContainer,
              styles.textInput,
              styles.thinInput,
              pressed && styles.pressedInput,
              errors?.['backside-image'] && styles.missingInput,
            ]}
            // eslint-disable-next-line react/jsx-handler-names
            onPress={backsideImagePicker.openImagePicker}>
            <Typography style={appStyles.fullSize}>
              {getImageName(backsideImagePicker.image, oldBacksideImageUrl)}
            </Typography>
            <Pressable
              hitSlop={20}
              style={styles.imageInputIcon}
              disabled={!onBacksideImageClearPress}
              onPress={onBacksideImageClearPress}>
              <SvgIcon
                name={!onBacksideImageClearPress ? 'chevron' : 'close'}
                width={20}
                height={20}
                color={Colors['layout.black']}
              />
            </Pressable>
          </Pressable>
          <Separator height={24} />
          <Typography weight="medium" style={styles.inputLabel}>
            {t('summaryLabel')}
          </Typography>
          <Separator height={8} />
          <TypographyInput
            multiline
            weight="medium"
            value={summary}
            placeholder={loading ? undefined : t('summaryPlaceholder')}
            placeholderTextColor={convertHexToRGBA(Colors['layout.black'], 0.5)}
            style={[
              styles.textInput,
              styles.descriptionInput,
              focus === 'summary' && styles.focusedInput,
              errors.summary && styles.missingInput,
              !!summaryWarning && styles.warningInput,
            ]}
            onChangeText={setSummary}
            onBlur={getOnBlur('summary')}
            onFocus={() => setFocus('summary')}
          />
          {!!summaryWarning && (
            <Typography weight="light" style={[styles.summaryWarningText]}>
              {summaryWarning}
            </Typography>
          )}
          <Separator height={insets.bottom + 100} />
        </ScrollView>
        <View style={[styles.buttonContainer, { bottom: insets.bottom + 20 }]}>
          <TouchableOpacity style={styles.saveButton} onPress={onSavePress}>
            <ButtonText size="medium" color="layout.white">
              {i18n.t('common.save')}
            </ButtonText>
          </TouchableOpacity>
          <Separator width={18} />
          <TouchableOpacity style={styles.cancelButton} onPress={goBack}>
            <ButtonText size="medium" color="layout.black">
              {i18n.t('common.cancel')}
            </ButtonText>
          </TouchableOpacity>
        </View>
      </View>

      <PreviewRegenerateConfirmationModal
        isVisible={isPreviewRegenerateConfimationModalVisible}
        onConfirmPress={submit}
        onRequestClose={() =>
          setIsPreviewRegenerateConfimationModalVisible(false)
        }
      />
    </WebModal>
  )
}

const placeholderTextColor = convertHexToRGBA(Colors['layout.black'], 0.5)
const borderColor = convertHexToRGBA(Colors['layout.gray'], 0.5)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors['layout.white'],
  },
  header: {
    borderColor,
    paddingTop: 15,
    paddingBottom: 16,
    borderBottomWidth: 1,
    paddingHorizontal: 24,
    ...appStyles.inlineContainer,
    justifyContent: 'space-between',
    backgroundColor: Colors['layout.white'],
  },
  titleText: {
    flex: 1,
    fontSize: 16,
    textAlign: 'center',
  },
  scrollContentContainer: {
    paddingHorizontal: 25,
  },
  inputLabel: {
    fontSize: 16,
  },
  textInput: {
    borderColor,
    fontSize: 14,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 11,
    paddingHorizontal: 17,
  },
  thinInput: {
    height: 54,
  },
  focusedInput: {
    borderWidth: 1,
    borderColor: Colors['brand.action'],
  },
  missingInput: {
    borderWidth: 1,
    borderColor: Colors['brand.red'],
  },
  warningInput: {
    borderWidth: 1,
    borderColor: Colors['timeline.orange'],
  },
  descriptionInput: {
    minHeight: 120,
    maxHeight: 500,
    textAlignVertical: 'top',
  },
  pressedInput: {
    backgroundColor: convertHexToRGBA(Colors['layout.gray'], 0.1),
  },
  imageInputIcon: {
    marginLeft: 'auto',
  },
  summaryWarningText: {
    fontSize: 15,
    opacity: 0.7,
    paddingLeft: 6,
    color: Colors['timeline.yellow-shading-dark'],
  },
  buttonContainer: {
    ...appStyles.row,

    bottom: 0,
    width: '100%',
    maxWidth: 500,
    position: 'absolute',
    paddingHorizontal: 24,
  },
  saveButton: {
    ...appStyles.center,
    flex: 2,
    padding: 15,
    borderRadius: 20,
    backgroundColor: Colors['layout.dark'],
  },
  cancelButton: {
    ...appStyles.center,
    ...appStyles.heavyShadow,
    flex: 1,
    padding: 15,
    borderRadius: 20,
    backgroundColor: Colors['layout.white'],
  },
})
